import { useEffect, useState } from 'react';
import {
    Box, Card, CardHeader, CardContent, Divider, Typography, TextField, Button,
    Switch, FormControlLabel, Select, MenuItem, LinearProgress, Alert, Stack,
} from '@mui/material';
import { getAiSettings, saveAiSettings, resetAiUsage, testAiSettings } from '../../api/generalSettingsApi';

const PP = "'Poppins', sans-serif";

export default function AiSettings() {
    const [cfg, setCfg] = useState({ enable: false, provider: 'openai', claudeKey: '', openAiKey: '', model: '', tokenLimit: 0, tokensUsed: 0 });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = async () => {
        try { const r = await getAiSettings(); setCfg(c => ({ ...c, ...r.data })); } catch { /* not super admin / not set */ }
    };
    useEffect(() => { load(); }, []);

    const set = (patch) => setCfg(c => ({ ...c, ...patch }));

    const save = async () => {
        setBusy(true); setMsg(null);
        try {
            // Don't send back the masked placeholder — leave the stored key untouched.
            const payload = { ...cfg };
            if ((payload.claudeKey || '').includes('*')) payload.claudeKey = '';
            if ((payload.openAiKey || '').includes('*')) payload.openAiKey = '';
            await saveAiSettings(payload);
            setMsg({ type: 'success', text: 'Saved.' });
            await load();
        } catch (e) {
            setMsg({ type: 'error', text: e?.response?.data?.message || e?.response?.data || 'Save failed.' });
        } finally { setBusy(false); }
    };

    const doTest = async () => {
        setBusy(true); setMsg(null);
        try {
            const r = await testAiSettings();
            setMsg({ type: r.data?.ok ? 'success' : 'error', text: r.data?.message || (r.data?.ok ? 'OK' : 'Failed') });
        } catch (e) { setMsg({ type: 'error', text: e?.response?.data?.message || 'Test failed.' }); }
        finally { setBusy(false); }
    };

    const doReset = async () => {
        setBusy(true);
        try { await resetAiUsage(); await load(); setMsg({ type: 'success', text: 'Usage counter reset.' }); }
        catch { setMsg({ type: 'error', text: 'Reset failed.' }); }
        finally { setBusy(false); }
    };

    const used = Number(cfg.tokensUsed) || 0;
    const tIn = Number(cfg.tokensIn) || 0;
    const tOut = Number(cfg.tokensOut) || 0;
    const limit = Number(cfg.tokenLimit) || 0;
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const over = limit > 0 && used >= limit;
    // Rough cost estimate (USD per 1M tokens) — gpt-4o-mini vs Claude Haiku ballpark.
    const price = cfg.provider === 'openai' ? { i: 0.15, o: 0.60 } : { i: 1.0, o: 5.0 };
    const costUsd = (tIn / 1e6) * price.i + (tOut / 1e6) * price.o;

    return (
        <Box sx={{ maxWidth: 720 }}>
            <Card sx={{ mb: 2 }}>
                <CardHeader title="AI Provider" titleTypographyProps={{ variant: 'h6', fontFamily: PP }}
                    subheader="Used for Telegram sale parsing, photo reading, and document classification." subheaderTypographyProps={{ fontFamily: PP, fontSize: '0.8rem' }} />
                <Divider />
                <CardContent>
                    {msg && <Alert severity={msg.type} sx={{ mb: 2, fontFamily: PP }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

                    <FormControlLabel control={<Switch checked={!!cfg.enable} onChange={e => set({ enable: e.target.checked })} />}
                        label="Enable AI" sx={{ mb: 1, '& .MuiFormControlLabel-label': { fontFamily: PP, fontWeight: 600 } }} />

                    <Stack spacing={2}>
                        <Box>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>Provider</Typography>
                            <Select size="small" fullWidth value={cfg.provider || 'claude'} onChange={e => set({ provider: e.target.value, model: '' })} sx={{ fontFamily: PP }}>
                                <MenuItem value="claude">Claude (Anthropic) — reads PDFs + images</MenuItem>
                                <MenuItem value="openai">ChatGPT (OpenAI) — images only</MenuItem>
                            </Select>
                        </Box>

                        <TextField label="Claude API key" size="small" fullWidth value={cfg.claudeKey || ''}
                            onChange={e => set({ claudeKey: e.target.value })} placeholder="sk-ant-..."
                            InputLabelProps={{ sx: { fontFamily: PP } }} inputProps={{ sx: { fontFamily: PP } }} />
                        <TextField label="OpenAI API key" size="small" fullWidth value={cfg.openAiKey || ''}
                            onChange={e => set({ openAiKey: e.target.value })} placeholder="sk-..."
                            InputLabelProps={{ sx: { fontFamily: PP } }} inputProps={{ sx: { fontFamily: PP } }} />
                        <TextField label="Model" size="small" fullWidth value={cfg.model || ''}
                            onChange={e => set({ model: e.target.value })}
                            placeholder={cfg.provider === 'openai' ? 'gpt-4o-mini' : 'claude-haiku-4-5-20251001'}
                            helperText="Leave blank to use the default for the provider."
                            InputLabelProps={{ sx: { fontFamily: PP } }} inputProps={{ sx: { fontFamily: PP } }} FormHelperTextProps={{ sx: { fontFamily: PP } }} />
                    </Stack>
                </CardContent>
            </Card>

            <Card>
                <CardHeader title="Token budget & usage" titleTypographyProps={{ variant: 'h6', fontFamily: PP }} />
                <Divider />
                <CardContent>
                    <TextField label="Monthly token limit (0 = unlimited)" size="small" type="number" value={cfg.tokenLimit || 0}
                        onChange={e => set({ tokenLimit: Number(e.target.value) || 0 })} sx={{ mb: 0.5, width: 300 }}
                        helperText="Resets automatically on the 1st of each month (MYT)."
                        InputLabelProps={{ sx: { fontFamily: PP } }} inputProps={{ sx: { fontFamily: PP } }} FormHelperTextProps={{ sx: { fontFamily: PP } }} />

                    <Typography sx={{ fontFamily: PP, fontSize: '0.85rem', mb: 0.25, mt: 1.5 }}>
                        Used this month: <b>{used.toLocaleString()}</b> tokens{limit > 0 ? ` / ${limit.toLocaleString()} (${pct}%)` : ' (no limit)'}
                    </Typography>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: 'text.secondary', mb: 0.5 }}>
                        Input: {tIn.toLocaleString()} · Output: {tOut.toLocaleString()} · Est. cost ≈ USD ${costUsd.toFixed(4)}
                    </Typography>
                    {limit > 0 && <LinearProgress variant="determinate" value={pct} color={over ? 'error' : 'primary'} sx={{ height: 8, borderRadius: 4, mb: 1 }} />}
                    {over && <Alert severity="warning" sx={{ fontFamily: PP, mb: 1 }}>Over budget — AI is paused until you raise the limit or reset usage.</Alert>}
                    <Button size="small" variant="outlined" disabled={busy} onClick={doReset} sx={{ fontFamily: PP, textTransform: 'none' }}>Reset usage counter</Button>
                </CardContent>
            </Card>

            <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="contained" disabled={busy} onClick={save} sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 700 }}>
                    {busy ? 'Saving…' : 'Save AI Settings'}
                </Button>
                <Button variant="outlined" disabled={busy} onClick={doTest} sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600 }}>
                    Test connection
                </Button>
            </Box>
        </Box>
    );
}
